import React, { Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import LoadingSpinner from "./components/LoadingSpinner";
import { Login } from "./pages/Login";
import { MustResetPassword } from "./pages/MustResetPassword";
import Layout from "./components/Layout";

// Lazy-loaded page components for code splitting
const NotFound = React.lazy(() => import("./pages/NotFound"));

// Intl Orders views
const OrdersListPage = React.lazy(() => import("./pages/OrdersListPage"));
const OrderDetailPage = React.lazy(() => import("./pages/OrderDetailPage"));
const StockPage = React.lazy(() => import("./pages/StockPage"));

// Admin views (role-gated: requiredRole="admin")
const AdminCatalogPage = React.lazy(() => import("./pages/admin/AdminCatalogPage"));
const AdminPartnersPage = React.lazy(() => import("./pages/admin/AdminPartnersPage"));
const AdminSettingsPage = React.lazy(() => import("./pages/admin/AdminSettingsPage"));
const AdminReportsPage = React.lazy(() => import("./pages/admin/AdminReportsPage"));

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
    },
  },
});

/** Component for Suspense fallback during lazy loading */
const LazyLoadingFallback = (): React.JSX.Element => <LoadingSpinner size="large" />;

/**
 * Renders lazy-loaded route element with Suspense boundary
 * Ensures proper type safety for React.lazy components
 */
const withLazySuspense = (component: React.LazyExoticComponent<React.FC>): React.JSX.Element => (
  <Suspense fallback={<LazyLoadingFallback />}>{React.createElement(component)}</Suspense>
);

/**
 * Main App component - Intl Orders Portal (partner-facing)
 * Provides routing, authentication, and query client context.
 *
 * Routes:
 * - /login                public
 * - /must-reset-password  authenticated, outside Layout
 * - /orders               protected (orders list)
 * - /orders/:id           protected (order detail)
 * - /stock                protected (3PL stock)
 * - /admin                admin-only, redirects to /admin/catalog
 * - /admin/catalog        admin-only (catalog allow-list)
 * - /admin/partners       admin-only (3PL partner accounts)
 * - /admin/settings       admin-only (runtime stock-gate config)
 */
function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />

              {/* Password Reset Route (Authenticated but not in Layout) */}
              <Route path="/must-reset-password" element={<MustResetPassword />} />

              {/* Protected Routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Navigate to="/orders" replace />} />

                        {/* Intl Orders Routes */}
                        <Route path="/orders" element={withLazySuspense(OrdersListPage)} />
                        <Route path="/orders/:id" element={withLazySuspense(OrderDetailPage)} />
                        <Route path="/stock" element={withLazySuspense(StockPage)} />

                        {/* Admin Routes (nested role guard — outer ProtectedRoute above only checks auth) */}
                        <Route
                          path="/reports"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              {withLazySuspense(AdminReportsPage)}
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/admin" element={<Navigate to="/admin/catalog" replace />} />
                        <Route
                          path="/admin/catalog"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              {withLazySuspense(AdminCatalogPage)}
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/partners"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              {withLazySuspense(AdminPartnersPage)}
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/settings"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              {withLazySuspense(AdminSettingsPage)}
                            </ProtectedRoute>
                          }
                        />

                        {/* 404 Catch-all Route - Must be last */}
                        <Route path="*" element={withLazySuspense(NotFound)} />
                      </Routes>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
